import React, { useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RevealWrapper } from '../hooks/useReveal';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { 
  Zap, Shield, Server, Gauge, Cloud, Lock, 
  FileCheck, Users, Calendar, FolderOpen, 
  Share2, Bell, Database, Activity 
} from 'lucide-react';

export const FeaturesPage = () => {
  // SEO: Update document title and meta description
  useEffect(() => {
    document.title = 'Nextcloud Hosting Features | Cluster, S3, HA | Wolkenhelden';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Alle Features unseres Managed Nextcloud Hostings: hochverfügbarer Cluster, S3-Storage, Hochverfügbarkeit, automatische Backups, Collabora Office, Antivirus und mehr.');
    }
  }, []);

  const mainFeatures = [
    {
      icon: Zap,
      title: 'Performance, die du spürst',
      description: 'Wenn dein Team arbeitet, zählt jede Sekunde. Unsere Architektur ist auf schnelle Zugriffe und Stabilität gebaut.',
      highlights: ['S3-Objektspeicher', 'Cluster-Skalierung', 'Optimierte Delivery']
    },
    {
      icon: Shield,
      title: 'Hochverfügbarkeit',
      description: 'Ausfälle einzelner Komponenten sind einkalkuliert – deine Cloud bleibt erreichbar.',
      highlights: ['Multi-Node Setup', 'Automatisches Failover', 'Selbstheilende Systeme']
    },
    {
      icon: Lock,
      title: 'Security-First',
      description: 'Updates, Härtung, Monitoring. Du bekommst Sicherheit, ohne dich darum kümmern zu müssen.',
      highlights: ['Automatische Updates', 'Security-Patches', 'Monitoring 24/7']
    },
    {
      icon: Users,
      title: 'Team-Power (Zusammenarbeit)',
      description: 'Mit Collabora Office bearbeiten deine Helden Dokumente gemeinsam – direkt in eurer Nextcloud.',
      highlights: ['Echtzeit-Kollaboration', 'Office-Dokumente', 'Versionierung']
    },
    {
      icon: FileCheck,
      title: 'Antivirus (optional)',
      description: 'Extra Schutzebene für Uploads – wenn du es brauchst, schaltest du es einfach dazu.',
      highlights: ['Upload-Scan', 'Malware-Erkennung', 'Quarantäne']
    },
    {
      icon: Gauge,
      title: 'Turbo-Antrieb (HP Backend)',
      description: 'Für Nextcloud Talk & Video-Konferenzen – maximale Performance für echte Team-Kommunikation.',
      highlights: ['Talk & Video', 'Erhöhte Ressourcen', 'Optimierte DB']
    },
  ];

  const useCases = [
    { icon: FolderOpen, title: 'Dateien & Ordner', description: 'Sync & Share wie du es brauchst – auf allen Geräten synchron.' },
    { icon: Users, title: 'Team-Helden arbeiten zusammen', description: 'Dokumente, Tabellen, Präsentationen – gemeinsam bearbeiten in Echtzeit.' },
    { icon: Calendar, title: 'Kalender, Kontakte, Aufgaben', description: 'Alles an einem Ort – synchronisiert mit deinem Team.' },
    { icon: Share2, title: 'Extern teilen', description: 'Links, Rechte, Ablaufdaten – du hast die volle Kontrolle.' },
  ];

  const technicalFeatures = [
    { icon: Database, title: 'S3-Objektspeicher', description: 'Skaliert sauber mit deinen Daten. Schnell & robust als primärer Storage.' },
    { icon: Server, title: 'Cluster-Orchestrierung', description: 'Rolling Updates, Self-Healing und echte Hochverfügbarkeit.' },
    { icon: Activity, title: 'Monitoring & Alerting', description: 'Wir überwachen deine Cloud rund um die Uhr – proaktiv, nicht reaktiv.' },
    { icon: Bell, title: 'Managed Updates', description: 'Security-Patches und Feature-Updates – ohne dass du nachts wach liegst.' },
  ];

  return (
    <div className="py-16 md:py-24" data-testid="features-page">
      {/* Hero Section */}
      <section className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <RevealWrapper>
          <div className="mb-16 text-center">
            <Badge variant="secondary" className="mb-4">Features</Badge>
            <h1 className="font-display text-4xl font-semibold tracking-tight sm:text-5xl">
              Managed Nextcloud, die einfach läuft
            </h1>
            <p className="mt-4 text-lg text-muted-foreground md:text-xl max-w-3xl mx-auto">
              Hochverfügbar, selbstheilend, ultraschnell. Managed Nextcloud im HA-Cluster 
              mit S3-Storage – inkl. optional Collabora Office und Antivirus.
            </p>
          </div>
        </RevealWrapper>

        {/* Main Features Grid */}
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3 mb-24">
          {mainFeatures.map((feature, i) => {
            const IconComponent = feature.icon;
            return (
              <RevealWrapper key={i} delay={i * 50}>
                <Card 
                  className="glass-card h-full transition-all hover:shadow-lg hover:-translate-y-1"
                  data-testid={`feature-card-${i}`}
                >
                  <CardHeader>
                    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                      <IconComponent className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="font-display text-xl">{feature.title}</CardTitle>
                    <CardDescription className="text-base">{feature.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        {feature.highlights[0]}
                      </li>
                      <li className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        {feature.highlights[1]}
                      </li>
                      <li className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                        {feature.highlights[2]}
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </RevealWrapper>
            );
          })}
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="bg-muted/30 py-16 md:py-24">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <RevealWrapper>
            <div className="mb-12 text-center">
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                Für Teams gemacht
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Alles, was du für die Zusammenarbeit brauchst.
              </p>
            </div>
          </RevealWrapper>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {useCases.map((useCase, i) => {
              const UseCaseIcon = useCase.icon;
              return (
                <RevealWrapper key={i} delay={i * 75}>
                  <Card className="glass-card h-full text-center" data-testid={`usecase-${i}`}>
                    <CardHeader>
                      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                        <UseCaseIcon className="h-8 w-8 text-primary" />
                      </div>
                      <CardTitle className="font-display text-lg">{useCase.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{useCase.description}</p>
                    </CardContent>
                  </Card>
                </RevealWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* Technical Features Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <RevealWrapper>
            <div className="mb-12 text-center">
              <h2 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
                Die Technik dahinter
              </h2>
              <p className="mt-4 text-lg text-muted-foreground">
                Gebaut auf einer Plattform, die für Verfügbarkeit und Tempo optimiert ist.
              </p>
            </div>
          </RevealWrapper>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {technicalFeatures.map((tech, i) => {
              const TechIcon = tech.icon;
              return (
                <RevealWrapper key={i} delay={i * 75}>
                  <div className="text-center" data-testid={`tech-feature-${i}`}>
                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                      <TechIcon className="h-7 w-7 text-primary" />
                    </div>
                    <h3 className="font-display text-lg font-semibold mb-2">{tech.title}</h3>
                    <p className="text-sm text-muted-foreground">{tech.description}</p>
                  </div>
                </RevealWrapper>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <RevealWrapper>
            <Card className="glass-card border-primary/20 text-center">
              <CardHeader>
                <CardTitle className="font-display text-3xl sm:text-4xl">
                  Hol dir deine Cloud – wir kümmern uns um den Rest.
                </CardTitle>
                <CardDescription className="text-lg mt-4">
                  Konfiguriere deine Wolke und leg direkt los.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild size="lg" className="transition-transform hover:scale-105">
                  <Link to="/preise" data-testid="features-cta-button">
                    Cloud zusammenstellen
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </RevealWrapper>
        </div>
      </section>
    </div>
  );
};
